import styled from 'styled-components'

export const Container = styled.div`
  width: 100%;
  height: auto;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: stretch;
`

export const SecurityTransitionOverlay = styled.div<{ persistent?: boolean }>`
  position: ${(props) => (props.persistent ? 'relative' : 'absolute')};
  width: ${(props) => (props.persistent ? 'min(100%, 700px)' : 'auto')};
  box-sizing: border-box;
  inset: 0;
  z-index: 3;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px;
  background: rgba(255, 255, 255, 0.97);
  color: #183449;
  border-radius: ${(props) => (props.persistent ? '22px' : 'inherit')};
  box-shadow: ${(props) => (props.persistent ? '0 18px 42px rgba(24, 52, 73, 0.12)' : 'none')};
  text-align: center;

  ${(props) =>
    props.persistent &&
    `
      margin: 0 auto;
      border: 1px solid rgba(199, 215, 226, 0.9);
    `}

  h2 {
    margin: 18px 0 8px;
    color: #183449;
    font-family: 'Public Sans', sans-serif;
    font-size: 1.35rem;
    font-weight: 800;
  }

  p {
    max-width: 330px;
    margin: 0;
    color: #587084;
    font-family: 'Public Sans', sans-serif;
    font-size: 0.95rem;
    line-height: 1.55;
  }

  span {
    margin-top: 14px;
    color: #587084;
    font-family: 'Public Sans', sans-serif;
    font-size: 0.82rem;
    font-weight: 700;
  }
`

export const SecurityTransitionSeal = styled.div`
  display: grid;
  width: 58px;
  height: 58px;
  place-items: center;
  border-radius: 50%;
  background: #edf4fa;
  color: #2d749c;
  font-family: 'Public Sans', sans-serif;
  font-size: 1.8rem;
  font-weight: 800;
`

export const SecurityTransitionProgress = styled.div`
  position: relative;
  width: min(100%, 320px);
  height: 7px;
  margin-top: 26px;
  overflow: hidden;
  border-radius: 999px;
  background: #e8eff5;

  &::after {
    position: absolute;
    top: 0;
    left: -40%;
    width: 40%;
    height: 100%;
    border-radius: inherit;
    background: linear-gradient(90deg, #c6016b, #2d749c);
    content: '';
    animation: secure-login-progress 1.1s ease-in-out infinite;
  }

  @media (prefers-reduced-motion: reduce) {
    &::after {
      left: 30%;
      animation: none;
    }
  }

  @keyframes secure-login-progress {
    from {
      transform: translateX(0);
    }

    to {
      transform: translateX(350%);
    }
  }
`

export const SecurityTransitionRetryButton = styled.button`
  margin-top: 24px;
  padding: 11px 22px;
  border: 1px solid #2d749c;
  border-radius: 999px;
  background: #ffffff;
  color: #2d749c;
  cursor: pointer;
  font-family: 'Public Sans', sans-serif;
  font-size: 0.9rem;
  font-weight: 800;

  &:hover {
    background: #edf4fa;
  }

  &:focus-visible {
    outline: 3px solid rgba(45, 116, 156, 0.35);
    outline-offset: 3px;
  }
`

export const FormBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
`

export const FormField = styled.div`
  display: flex;
  flex-direction: column;
`

export const ActionsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;
  margin-top: 8px;

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: stretch;
  }
`

export const SecondaryAction = styled.div`
  display: flex;
  align-items: center;
  min-width: fit-content;

  @media (max-width: 640px) {
    justify-content: center;
  }
`

export const ForgotPassword = styled.a`
  color: #2d749c;
  font-size: 14px;
  font-weight: 700;
  text-decoration: none;
  cursor: pointer;
  transition: color 0.18s ease;

  &:hover {
    color: #c6016b;
  }
`

export const ErrorText = styled.p`
  color: #c93b55;
  font-size: 12px;
  font-weight: 700;
  text-decoration: none;
  margin-top: 10px;
`

export const MfaInstructions = styled.p`
  width: min(100%, 520px);
  margin: 0;
  color: #587084;
  font-size: 0.95rem;
  line-height: 1.55;
  text-align: center;

  span {
    display: block;
  }

  strong {
    color: #183449;
    font-weight: 800;
  }
`

export const MfaStatusRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`

export const MfaStatusItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 12px 14px;
  border: 1px solid #dce8f1;
  border-radius: 12px;
  background: #f6f9fc;

  span {
    color: #587084;
    font-size: 0.75rem;
    font-weight: 700;
    letter-spacing: 0.02em;
    text-transform: uppercase;
  }

  strong {
    color: #183449;
    font-size: 0.95rem;
  }
`

export const MfaStatusAction = styled.button`
  display: flex;
  flex-direction: column;
  gap: 3px;
  align-items: center;
  justify-content: center;
  min-height: 68px;
  padding: 12px 14px;
  border: 1px solid #dce8f1;
  border-radius: 12px;
  background: #f6f9fc;
  color: #183449;
  cursor: pointer;
  font-family: inherit;

  span {
    color: #587084;
    font-size: 0.75rem;
    font-weight: 700;
    letter-spacing: 0.02em;
    text-transform: uppercase;
  }

  strong {
    color: #2d749c;
    font-size: 0.95rem;
  }

  &:hover:not(:disabled) {
    border-color: #a9c8da;
    background: #edf4fa;
  }

  &:focus-visible {
    outline: 3px solid rgba(45, 116, 156, 0.35);
    outline-offset: 2px;
  }

  &:disabled {
    cursor: not-allowed;

    strong {
      color: #587084;
    }
  }
`

export const MfaRememberDevice = styled.label`
  display: flex;
  align-items: center;
  gap: 9px;
  color: #587084;
  font-size: 0.85rem;
  line-height: 1.4;

  input {
    width: 16px;
    height: 16px;
    accent-color: #2d749c;
  }
`
